import { Component, type ReactNode } from 'react';
import { isChunkLoadError } from '@/shared/utils/chunkLoadError';
import { markAppStale } from '@/shared/utils/appVersionStore';

interface ChunkErrorBoundaryProps {
    children: ReactNode;
    fallback: ReactNode;
    /** 這個值改變（例如換頁）時，清除錯誤狀態重新嘗試渲染 */
    resetKey?: unknown;
}

interface ChunkErrorBoundaryState {
    error: unknown;
    hasError: boolean;
}

/**
 * 最後一道防線：只接 chunk 載入錯誤，避免整棵 React 樹卸載而弄丟使用者填到一半的表單。
 * 其他錯誤在 render 時原樣重新丟出，交給上層處理，行為與沒有這個邊界時相同。
 */
export class ChunkErrorBoundary extends Component<
    ChunkErrorBoundaryProps,
    ChunkErrorBoundaryState
> {
    state: ChunkErrorBoundaryState = { error: null, hasError: false };

    static getDerivedStateFromError(error: unknown): ChunkErrorBoundaryState {
        return { error, hasError: true };
    }

    componentDidCatch(error: unknown): void {
        if (isChunkLoadError(error)) markAppStale();
    }

    componentDidUpdate(previousProps: ChunkErrorBoundaryProps): void {
        if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null, hasError: false });
        }
    }

    render(): ReactNode {
        if (!this.state.hasError) return this.props.children;
        if (!isChunkLoadError(this.state.error)) throw this.state.error;
        return this.props.fallback;
    }
}
